import { createRoot } from 'react-dom/client';
import { PROTOCOL_VERSION, ipcModeEnabled } from '@usability-suite/protocol-client';
import { ThemeProvider } from '@usability-suite/ui';
import './styles.css';
import { App } from './App';
import { checkAndClearOnVersionMismatch } from './storage_version';
import { UpdateBanner } from './sw/UpdateBanner';
import { registerServiceWorker } from './sw/register';

const root = document.getElementById('root');
if (!root) {
  throw new Error('shell: #root not found');
}

function isDevBuild(): boolean {
  // Same shim as ClearLocalStorageButton.tsx — Vite replaces
  // `import.meta.env.DEV` at build time; non-Vite contexts leave
  // the property undefined.
  try {
    const v = (import.meta as unknown as { env?: { DEV?: unknown } }).env;
    return v?.DEV === true;
  } catch {
    return false;
  }
}

// Boot-time stored-data shape guard. Dev builds wipe everything if
// `STORAGE_SCHEMA_VERSION` doesn't match what's on disk (a developer
// bumped a stored shape incompatibly); production builds only record
// the version so a real lab's data is never silently deleted. See
// storage_version.ts for the contract. Fire and continue — the
// typical case (no mismatch) resolves on the next microtask and
// React renders immediately; the rare wipe path triggers a reload
// that supersedes whatever's already rendering.
void checkAndClearOnVersionMismatch({
  isDev: isDevBuild(),
  returnHash: '#/',
});

// vite.config.ts emits the SW bundle at /sw.js (a separate Rollup
// entry, not a hashed asset, so the scope can be '/'). Registration
// is gated on the browser supporting Service Workers and the page
// being served from a secure origin (https:// or localhost) —
// failures are silent.
//
// `mode` tells the SW whether this deployment expects it to serve
// /api/* from the in-browser broker (`broker`, when `?ipc=1`) or
// pass through to a real backend (`passthrough` — desktop with
// controllerd, or Cloudflare DO). Without this the SW would default
// to passthrough and IPC-mode tabs would silently miss broker
// intercept; with it, controllerd deployments see real network
// behavior and IPC tabs see broker behavior.
//
// bundleProtocolVersion is the wire version this bundle was built
// against. A mismatch with the controlling SW means the SW is on a
// newer wire format; the UpdateBanner forces a reload.
void registerServiceWorker({
  swUrl: '/sw.js',
  bundleProtocolVersion: PROTOCOL_VERSION,
  mode: ipcModeEnabled() ? 'broker' : 'passthrough',
});

createRoot(root).render(
  <ThemeProvider>
    <App />
    <UpdateBanner />
  </ThemeProvider>,
);
